import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * AppLandingPage - Landing page do aplicativo Meu Consumo
 * Equivalente a: redirect/app.html.erb
 */
export default function AppLandingPage() {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleDownload = (platform: 'ios' | 'android') => {
    if (platform === 'ios') {
      window.location.href = 'https://apps.apple.com/br/app/laager-meu-consumo/id1234387091';
    } else {
      window.location.href = 'https://play.google.com/store/apps/details?id=br.com.laager.meuconsumo';
    }
  };

  return (
    <div className="app-landing">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 py-4 bg-gray-100">
        <div className="w-full px-4">
          <a className="text-4xl font-semibold ml-4" href="#">
            Meu Consumo App
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="w-full px-4" style={{ paddingTop: '102px', paddingBottom: '56px' }}>
        <div className="flex flex-wrap items-center justify-center min-h-screen">
          <div className="w-11/12 xl:w-11/12">
            <div className="flex flex-wrap items-center justify-between">
              <div className="w-full xl:w-1/2 pt-5">
                <h1 className="text-5xl md:text-6xl font-semibold">
                  {t('views.landing.hero_title', 'Inovação para desafios modernos')}
                </h1>
                <p className="text-xl mt-3 font-medium">
                  {t('views.landing.hero_text', '"Somos referência na América Latina em inovação e desenvolvimento de soluções para eficiência energética e hídrica com utilização de sistemas e comunicação remota (IoT) para automação de medidores de água, gás e energia."')}
                </p>
                <button 
                  type="button" 
                  className="bg-[#0d6efd] hover:bg-[#0b5ed7] text-white text-2xl font-medium py-3 px-6 rounded-lg mt-5"
                  onClick={() => setShowModal(true)}
                >
                  {t('views.landing.hero_cta', 'Confira seu consumo agora')}
                </button>
              </div>
              <div className="w-full xl:w-1/2 mt-3">
                <img 
                  src="https://meuconsumo.laager.com.br/assets/images/smartphone_in_hand_mockup_crop.png"
                  className="w-full h-auto mt-4 rounded-3xl" 
                  alt="Meu Consumo App" 
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Section */}
      <div className="w-full px-4">
        <div className="flex flex-wrap items-center justify-center">
          <div className="w-11/12 xl:w-11/12 my-5 py-5">
            <div className="flex flex-wrap items-center justify-between">
              <div className="w-full xl:w-1/2">
                <img 
                  src="https://meuconsumo.laager.com.br/assets/images/dia-a-dia.png" 
                  className="w-full h-auto rounded-3xl"
                  alt="Soluções para o dia a dia" 
                />
              </div>
              <div className="w-full xl:w-1/2">
                <h1 className="mt-5 text-5xl md:text-6xl font-semibold">
                  {t('views.landing.section2_title', 'Soluções inovadoras para o seu dia a dia')}
                </h1>
                <p className="text-xl mt-6 pt-4">
                  {t('views.landing.section2_text', 'Laager Tecnologias Renováveis, sediada em São Paulo, SP, dedica-se a transformar a forma como gerenciamos recursos hídricos, gasosos e elétricos com nosso aplicativo inovador de monitoramento de consumo. Nossa missão é capacitar indivíduos e empresas a otimizar o uso da água, do gás e da energia, promovendo sustentabilidade e eficiência.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="w-full px-4 bg-gray-200 py-5 mb-1">
        <div className="flex flex-wrap items-center justify-center">
          <div className="w-11/12 xl:w-3/4 my-4 py-4">
            <div className="flex flex-wrap items-center justify-center">
              <div className="w-full">
                <span className="inline-block rounded-full bg-gray-300 text-[#0d6efd] text-xl py-2 px-3">
                  {t('views.landing.badge', 'Poupe Recursos')}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap justify-between gap-5 mt-1">
              <div className="w-full xl:w-[calc(50%-10px)] pt-5">
                <div className="bg-white rounded-2xl h-full border-0">
                  <div className="p-4">
                    <i className="fas fa-tint fa-3x text-[#0d6efd] mb-3"></i>
                    <h3 className="text-xl font-semibold">{t('views.landing.water_title', 'Monitoramento de Água')}</h3>
                    <p className="text-gray-600">
                      {t('views.landing.water_text', 'Acompanhe seu consumo de água em tempo real e identifique padrões de uso.')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-full xl:w-[calc(50%-10px)] pt-5">
                <div className="bg-white rounded-2xl h-full border-0">
                  <div className="p-4">
                    <i className="fas fa-fire fa-3x text-yellow-500 mb-3"></i>
                    <h3 className="text-xl font-semibold">{t('views.landing.gas_title', 'Monitoramento de Gás')}</h3>
                    <p className="text-gray-600">
                      {t('views.landing.gas_text', 'Controle seu consumo de gás e receba alertas de consumo atípico.')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h5 className="text-lg font-semibold">{t('views.landing.modal_title', 'Baixe o App')}</h5>
              <button 
                type="button" 
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <div className="p-4 text-center">
              <p className="mb-4">{t('views.landing.choose_platform', 'Escolha sua plataforma:')}</p>
              <div className="flex flex-col gap-3">
                <button 
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white py-3 px-4 rounded-lg text-lg font-medium"
                  onClick={() => handleDownload('ios')}
                >
                  <i className="fab fa-apple mr-2"></i>
                  App Store (iOS)
                </button>
                <button 
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg text-lg font-medium"
                  onClick={() => handleDownload('android')}
                >
                  <i className="fab fa-google-play mr-2"></i>
                  Google Play (Android)
                </button>
              </div>
            </div>
            <div className="p-4 border-t">
              <p className="text-gray-500 text-sm">
                {t('views.landing.browser_access', 'Ou acesse pelo navegador:')} <a href="https://meuconsumo.laager.com.br" className="text-[#0d6efd] hover:underline">meuconsumo.laager.com.br</a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="mb-0">{t('views.landing.copyright', '© 2024 Laager Tecnologias Sustentáveis. Todos os direitos reservados.')}</p>
          <a href="/app/privacy" className="text-gray-400 hover:text-white">{t('views.registration.user_data.privacy_policy', 'Política de Privacidade')}</a>
        </div>
      </footer>
    </div>
  );
}
